import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, DialogActions, Divider, Skeleton } from '@mui/material';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getProjectCohortVariableViewPath,
  getProjectVariablePath,
} from '@verantos-dev/core-constants';
import { AvSlugClickProvider } from '@verantos-dev/feature-epi-chat';
import { ApiWorkflowDialog, Button } from '@verantos-dev/shared-components';

import { AvContentCard } from './AvContentCard';
import { useVariableDetail } from '../../api/useVariableDetail';

// OR-6115 shape: a click on an inline AV reference hands back a bare slug
// (``baseline_weight_60d_lbs``), an already-qualified knowledge id
// (``av.<slug>``), or a locked-library UUID — normalize to the id the
// variable-detail route expects.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface AvDenovoPreviewModalProps {
  /** Stable id used to namespace the dialog testid hierarchy. */
  id: string;
  /** Library AV UUID. Fetched lazily — query only fires when ``isOpen``. */
  analyticalVariableId: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Destination URL for the "View full" link in the modal footer.
   * When the caller opened the modal from inside a cohort criterion,
   * this should point at the cohort-scoped variable view route
   * (``/projects/<pid>/cohort/<cid>/variable/<vid>``) so the
   * destination's breadcrumb hook picks up the cohort context from
   * the URL itself — no state sidecar needed.
   */
  viewFullHref?: string;
  /**
   * OR-6234: resolve the variable even if it has been soft-deleted. Set only
   * when the modal is opened from another variable's verification result,
   * whose index-date anchor may since have been deleted (AC 6).
   */
  includeDeleted?: boolean;
}

// Min height keeps the dialog from collapsing to ~2 visible rows when
// the description is short and the SQL tab hasn't been clicked yet.
// Matches roughly the analytical-variable edit-page footprint so the
// two surfaces feel proportionate when compared.
const MODAL_CONTENT_MIN_HEIGHT = 360;

/**
 * Modal preview for SQL-first (av_denovo) library AVs viewed from
 * inside a cohort criterion card.
 *
 * The legacy ``AVPreviewModal`` wraps ``AnalyticalVariableCard``, which
 * renders the structured DSL tree. av_denovo rows have no DSL —
 * their ``jsonValue`` is null and their semantics are the SQL body
 * plus the manifest stamped in ``METADATA``. This modal swaps the
 * card body for :class:`AvContentCard` with no ``visibleTabs``
 * restriction so the popup renders the same tabset as the View Full
 * page (Description / SQL / Concepts / Decisions / Recall /
 * Verification — the manifest-enriched trio is gated behind
 * ``av_card_recall`` inside ``AvContentCard``).
 *
 * The detail fetch is gated on ``isOpen`` so the criterion list
 * doesn't trigger a fan-out of /analytical-variables calls just by
 * rendering — the query only fires when the user actually opens the
 * modal.
 */
export const AvDenovoPreviewModal = ({
  id,
  analyticalVariableId,
  isOpen,
  onClose,
  viewFullHref,
  includeDeleted = false,
}: AvDenovoPreviewModalProps) => {
  const navigate = useNavigate();
  // ``projectId`` from the URL gates the detail fetch (OR-6081 AC22).
  // ``cohortId`` is present when this modal was opened from inside a cohort
  // criterion card (route nests under ``cohort/:cohortId``) — absent for the
  // flat project-scoped mounts.
  const { projectId, cohortId } = useParams<{ projectId: string; cohortId?: string }>();
  const detailQuery = useVariableDetail(
    isOpen ? analyticalVariableId : null,
    projectId || null,
    null,
    {
      includeDeleted,
    }
  );
  const detail = detailQuery.data?.data ?? null;

  // Explicit handler so the call order is unambiguous: navigate first
  // (history.push runs synchronously here), then close. Keeps the
  // browser-native open-in-new-tab affordance on modified clicks.
  const handleViewFullClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!viewFullHref) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      navigate(viewFullHref);
      onClose();
    },
    [navigate, onClose, viewFullHref]
  );

  // OR-6306: an inline AV-slug reference inside the Description/SQL tabs
  // should route to that variable's detail view (cohort-scoped when this
  // modal itself is cohort-scoped) instead of the legacy knowledge-browser
  // fallback ``Markdown``/``CodeWithKnowledgeLinks`` use when no handler is
  // registered.
  const handleAvSlugClick = useCallback(
    (idOrSlug: string) => {
      if (!projectId) return;
      const knowledgeId =
        idOrSlug.startsWith('av.') || UUID_PATTERN.test(idOrSlug) ? idOrSlug : `av.${idOrSlug}`;
      navigate(
        cohortId
          ? getProjectCohortVariableViewPath(projectId, cohortId, knowledgeId)
          : getProjectVariablePath(projectId, knowledgeId)
      );
      onClose();
    },
    [navigate, onClose, projectId, cohortId]
  );

  if (!isOpen) return null;

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError && !detail;

  return (
    <ApiWorkflowDialog
      id={id}
      title=""
      size="large"
      displayButtons={false}
      onClose={onClose}
      isValidForSubmit={false}
      dividers={false}
      dialogContentStyleProps={{ padding: 0, display: 'flex', flexDirection: 'column' }}
      NonScrollBottomComponent={
        <>
          <Divider />
          <DialogActions data-testid={`${id}-actions`} sx={{ gap: 2 }}>
            {viewFullHref ? (
              // Plain anchor with an explicit click handler — keeps
              // cmd/ctrl-click open-in-new-tab semantics intact while
              // making the in-SPA navigation order deterministic
              // (navigate with state, then close). Plain ``<Link>``
              // worked in unit tests but lost state under live
              // unmount-during-click ordering.
              <a
                href={viewFullHref}
                onClick={handleViewFullClick}
                data-testid={`${id}-view-full`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  textDecoration: 'none',
                  color: 'inherit',
                  marginRight: 'auto',
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
                <span>View full</span>
              </a>
            ) : null}
            <Button
              id={`${id}-close-button`}
              btnType="primary"
              displayLabel="Close"
              onClickHandler={onClose}
            />
          </DialogActions>
        </>
      }
    >
      <Box
        sx={{
          minHeight: MODAL_CONTENT_MIN_HEIGHT,
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading ? (
          <Box sx={{ padding: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="text" width={240} height={32} />
            <Skeleton variant="rectangular" height={120} />
            <Skeleton variant="rectangular" height={200} />
          </Box>
        ) : isError || !detail ? (
          <Box data-testid={`${id}-error`} sx={{ padding: 3, color: 'error.main' }}>
            Could not load variable.
          </Box>
        ) : (
          <AvSlugClickProvider onClick={handleAvSlugClick}>
            <AvContentCard
              detail={detail}
              handleCloseDialog={onClose}
              data-testid={`${id}-content`}
            />
          </AvSlugClickProvider>
        )}
      </Box>
    </ApiWorkflowDialog>
  );
};
